import React from "react";
import { cn } from "../../utils/cn";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "outline" | "ghost";
  isLoading?: boolean;
  fullWidth?: boolean;
  color?: "primary" | "danger" | "success" | "warning" | "info";
  rounded?: "sm" | "md" | "lg" | "xl" | "full";
  size?: "sm" | "md" | "lg" | "xl";
}

const Button: React.FC<ButtonProps> = ({
  variant = "primary",
  isLoading,
  children,
  className,
  fullWidth = false,
  color = "primary",
  rounded = "md",
  size = "md",
  ...props
}) => {
  const colorMap = {
    primary: "primary",
    danger: "red",
    success: "green",
    warning: "yellow",
    info: "blue",
  };
  const base =
    "inline-flex items-center justify-center font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed";

  const variants = {
    primary: `bg-${colorMap[color]}-600 hover:bg-${colorMap[color]}-700 text-white focus:ring-${colorMap[color]}-500`,
    outline: `border border-${colorMap[color]}-300 text-${colorMap[color]}-700 bg-white hover:bg-${colorMap[color]}-50 focus:ring-${colorMap[color]}-500`,
    ghost: `text-${colorMap[color]}-600 hover:bg-${colorMap[color]}-100 focus:ring-${colorMap[color]}-500`,
  };

  const roundedMap = {
    sm: "rounded-sm",
    md: "rounded-md",
    lg: "rounded-lg",
    xl: "rounded-xl",
    full: "rounded-full",
  };

  const sizeMap = {
    sm: "px-3 py-1.5 text-xs",
    md: "px-4 py-2 text-sm",
    lg: "px-5 py-2.5 text-base",
    xl: "px-6 py-3 text-lg",
  };

  return (
    <button
      {...props}
      disabled={isLoading || props.disabled}
      className={cn(
        base,
        variants[variant],
        roundedMap[rounded],
        sizeMap[size],
        fullWidth ? "w-full" : "w-auto",
        className
      )}
    >
      {isLoading && (
        <svg
          className="animate-spin h-4 w-4 mr-2 text-current"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
          ></path>
        </svg>
      )}
      {children}
    </button>
  );
};

export default Button;
